/**
 * Écrans du player Live School : WAITING (salon, compte à rebours) / LIVE
 * (question + choix + chrono) / FINISHED (GG !, podium, classement).
 * Mobile-first.
 *
 * Design « arène » (migration 0658) : tout l'écran est sombre, la cover du
 * défi en fond (`LiveArena`) ; la question courante et le classement sont des
 * feuilles claires (`.theme-paper`) ; couleurs de la marque (bleu, or).
 */

import { TrousseToolsProvider } from '@/features/trousse/trousse-tools';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { sheetOpensByDefault } from '@/features/scratch-sheet/auto-open';
import { ParticipateSwitchRow } from '@/features/news-tabs/live-school-tab';
import {
  registeredLabel,
  useLiveRegistrationCounts,
  useLiveRegistrationMutations,
  useMyLiveRegistrations,
} from '@/lib/live-registrations';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  FileText,
  FlaskConical,
  ListChecks,
  MonitorX,
  RefreshCw,
  Send,
  Share2,
  Timer,
  Trophy,
  Users,
  XCircle,
} from 'lucide-react';
import {
  MarkdownMath,
  VisualBlocksRenderer,
  FeedbackPopup,
  Button,
  cn,
  type XpReward,
} from '@jeraisonne/ui';
import {
  ExerciseQuickNav,
  BookOpen,
  HelpCircle,
  type QuickNavItem,
} from '@/components/exercise-quick-nav';
import { CalculatorModal } from '@/components/calculator-modal';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { HoraRewardPop, MoveNudge, MovePanel, usePlayerMoves } from '@/features/moves';
import { useDeleteTestParticipation } from '@/lib/live-test';
import { liveCoverUrl } from '@/lib/live-covers';
import { shareContent } from '@/lib/native-share';
import { TeacherWaitingNote } from './teacher-waiting-note';
import type { StepParticipation } from '@/lib/live-step-participation';
import { finalAppreciation, finishHeadline, ordinalFr } from './final-appreciation';
import {
  useLiveQuiz,
  type LivePrize,
  type LiveChoice,
  type LiveContentBlock,
  type LiveFeedback,
  type RevealedStep,
} from './use-live-quiz';
import { LeaderboardTabs } from './leaderboard-tabs';
import { LivePrizeTile } from './live-prize-tile';
import { ClueHuntLive } from './clue-hunt-live';
import { goBackFromLive } from './live-back';
import { PrivateLobby } from '@/features/private-live/private-lobby';
import { PrivateLeaderboard } from '@/features/private-live/private-leaderboard';
import { PrivateLiveOutro } from '@/features/private-live/private-live-outro';
import {
  ProgressBubbles,
  ProgressPanel,
  ProgressToggleButton,
  useLiveProgress,
  useProgressPanelToggle,
} from '@/features/private-live/live-progress-view';
import {
  privateLiveUrl,
  usePrivateLive,
  usePrivateLiveBoard,
  type PrivateBoard,
} from '@/lib/private-live';
import { ScratchEntryCard } from '@/features/scratch-sheet/scratch-entry';
import { ScratchSheet } from '@/features/scratch-sheet/scratch-sheet';
import type { ScratchSession } from '@/features/scratch-sheet/scratch-session';
import { useLiveScratch, type LiveV2Sheet } from './use-live-scratch';
import {
  AnswerDock,
  AnswerKeys,
  PaperChoiceList,
  PaperColumn,
  PaperPage,
  FinalTeacherGrade,
  HandRanking,
  TeacherNote,
  choiceKeyLayout,
  dockMode,
  gradeOn20,
  toggleChoice,
  type HandRankingRow,
  type PaperQcm,
} from '@jeraisonne/ui/paper';
import { usePaperPlayerForMe } from '@/features/paper-player';
import {
  InlineSheet,
  SheetPalette,
  useCrossedChoices,
  useFoundChoices,
  useSessionState,
} from '@/features/scratch-sheet';
import { numbersInText } from '@/features/scratch-sheet/free-sheet';
import {
  freeSheetKey,
  useFreeSheet,
  useFreeV2Sheet,
} from '@/features/scratch-sheet/use-free-sheet';
import { LivePodium } from './live-podium';
import { useLiveStepPodium, type PodiumEntry } from '@/lib/live-podium';
import {
  ArenaHeader,
  ArenaIconButton,
  ArenaInfoList,
  ArenaPanel,
  LiveArena,
} from '@/features/live-ui/live-arena';
import { liveCtaClass } from '@/features/live-ui/live-cover-card';
import { formatScore } from '@/features/live-ui/ranking-list';
import { levelLabel, shortFormatLabel } from '@/features/live-ui/live-card-model';

/** Cast des visuels du payload (unknown[]) vers le type attendu par le renderer. */
type VisualBlocks = Parameters<typeof VisualBlocksRenderer>[0]['blocks'];

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** Décompose une durée (ms) en jours / heures / minutes / secondes. */
export function breakdownCountdown(ms: number): CountdownParts {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

/**
 * Segments à afficher : on masque les unités de tête nulles (pas de « 00 j » si
 * on est à quelques heures) mais on garde toujours minutes + secondes.
 */
export function countdownSegments(ms: number): { value: number; label: string }[] {
  const { days, hours, minutes, seconds } = breakdownCountdown(ms);
  const all = [
    { value: days, label: 'j', show: days > 0 },
    { value: hours, label: 'h', show: days > 0 || hours > 0 },
    { value: minutes, label: 'min', show: true },
    { value: seconds, label: 's', show: true },
  ];
  return all.filter((s) => s.show).map(({ value, label }) => ({ value, label }));
}

/** « 00:32 », « 01:05:09 » ; les jours à part (« 2 j »). PUR. */
export function clockLabel(ms: number): { days: number; clock: string } {
  const { days, hours, minutes, seconds } = breakdownCountdown(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock =
    days > 0 || hours > 0
      ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(minutes)}:${pad(seconds)}`;
  return { days, clock };
}

/**
 * Avancée du live (0..1) : questions passées + part écoulée de la question en
 * cours. Sert à la barre de progression du jeu. PUR.
 */
export function liveProgress(
  stepIndex: number,
  stepCount: number,
  timeLeftMs: number,
  stepDurationMs: number,
): number {
  if (stepCount <= 0) return 0;
  const inStep = stepDurationMs > 0 ? 1 - Math.min(1, Math.max(0, timeLeftMs / stepDurationMs)) : 0;
  return Math.min(1, Math.max(0, (Math.max(0, stepIndex) + inStep) / stepCount));
}

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export function LiveQuizScreen({
  niveau,
  userId,
  testChallengeId,
  challengeId,
  privateLive,
}: {
  niveau: string;
  userId: string;
  /** Défi à rejouer en mode TEST (staff/dev), ancré à l'instant de lancement. */
  testChallengeId?: string;
  /** Live précis à jouer (`?defi=`), sinon le live courant du niveau. */
  challengeId?: string;
  /** Défi privé (0650) : son code, et le prénom d'un invité. */
  privateLive?: { code: string; guestName?: string | null; isGuest?: boolean };
}) {
  const q = useLiveQuiz(niveau, testChallengeId, challengeId, {
    guestName: privateLive?.guestName ?? null,
  });
  const { profile } = useAuth();
  const goBack = () => goBackFromLive(profile?.role);
  // Sur la feuille : pas de popup de résultat, le prof écrit sous chaque question.
  const paper = usePaperPlayerForMe();
  const [stepNotes, setStepNotes] = useState<Record<number, LiveFeedback>>({});
  const feedback = q.pendingFeedback;
  const dismissFeedback = q.dismissFeedback;
  useEffect(() => {
    if (!paper || !feedback || feedback.stepIndex == null) return;
    const i = feedback.stepIndex;
    setStepNotes((prev) => ({ ...prev, [i]: feedback }));
    dismissFeedback();
  }, [paper, feedback, dismissFeedback]);
  const testMode = !!testChallengeId;
  const isPrivate = !!privateLive || !!q.privateInfo;
  const unit = q.scoring === 'hora' ? 'HORA' : 'pts';

  // Défi privé : sa fiche (cover, réglages) — même cache que le salon, sans
  // requête périodique pendant le jeu.
  const privateCode = privateLive?.code ?? q.privateInfo?.join_code ?? null;
  const privateQuery = usePrivateLive(isPrivate ? privateCode : null, userId, { poll: false });
  const privateData = privateQuery.data ?? null;

  // Cover : choisie (défi privé), sinon l'image de la campagne / de l'exercice,
  // sinon un preset tiré du défi. Rien tant que le défi n'est pas connu.
  const coverUrl = isPrivate
    ? liveCoverUrl({ path: privateData?.cover_path, seed: privateData?.id ?? privateCode })
    : q.challengeId
      ? liveCoverUrl({ fallbackUrl: q.coverUrl, seed: q.challengeId })
      : null;

  // Défi privé : classement du défi (invités compris), calculé à la demande.
  const privateBoard = usePrivateLiveBoard(
    isPrivate && (q.phase === 'live' || q.phase === 'finished') ? q.challengeId : null,
    q.phase === 'live' ? 10_000 : false,
  );

  // Rang national courant de l'élève (pour la popup de feedback + l'écran de fin).
  // Même clé de cache que l'onglet National → pas de fetch en double.
  const rankQuery = useQuery({
    queryKey: ['live-leaderboard', q.challengeId, 'national', ''],
    enabled: !isPrivate && !!q.challengeId && (q.phase === 'live' || q.phase === 'finished'),
    staleTime: 10_000,
    refetchInterval: q.phase === 'live' ? 20_000 : false,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<NationalBoard>('live-leaderboard', {
        body: { challenge: q.challengeId, scope: 'national', key: '' },
      });
      if (error) throw error;
      return data;
    },
  });
  const myRank = isPrivate
    ? (privateBoard.data?.me?.rank ?? null)
    : (rankQuery.data?.me?.rank ?? null);
  const myRankTotal = isPrivate
    ? (privateBoard.data?.me?.total ?? 0)
    : (rankQuery.data?.me?.total ?? 0);

  const tone = q.phase === 'live' ? 'hero' : q.phase === 'finished' ? 'strong' : 'medium';

  // Le même compte a ouvert ce défi ailleurs (autre onglet, fenêtre ou
  // appareil) : cette fenêtre est coupée (0674).
  if (q.superseded && q.phase !== 'finished') {
    return (
      <LiveArena coverUrl={coverUrl}>
        <SupersededDialog onTakeOver={() => void q.takeOver()} onLeave={goBack} />
      </LiveArena>
    );
  }

  if (q.phase === 'loading') {
    return (
      <LiveArena coverUrl={coverUrl}>
        <Centered>Chargement du défi…</Centered>
      </LiveArena>
    );
  }
  if (q.joinRefused === 'full') {
    return (
      <LiveArena coverUrl={coverUrl}>
        <Centered>
          <p className="mb-3">Ce défi est complet : il n’y a plus de place.</p>
          <Button onClick={() => (window.location.href = '/')}>Accueil</Button>
        </Centered>
      </LiveArena>
    );
  }
  if (q.phase === 'error') {
    return (
      <LiveArena coverUrl={coverUrl}>
        <Centered>
          <p className="mb-3">Impossible de charger le défi.</p>
          <Button onClick={() => q.refetchState()}>Réessayer</Button>
        </Centered>
      </LiveArena>
    );
  }
  if (q.phase === 'no-challenge') {
    return (
      <LiveArena coverUrl={coverUrl}>
        <Centered>
          <p className="font-display text-xl font-bold text-white">
            Aucun défi pour le niveau {levelLabel(niveau)}
          </p>
          <p className="text-ink-600 mt-2 text-sm">
            Le prochain Live School sera bientôt programmé. Reviens vite !
          </p>
          <button type="button" onClick={goBack} className={liveCtaClass('glass', 'mt-6')}>
            Retour
          </button>
        </Centered>
      </LiveArena>
    );
  }

  const title = q.title ?? privateData?.title ?? 'Le grand défi';
  const subtitle = isPrivate
    ? `${levelLabel(niveau)} · ${shortFormatLabel(privateData?.settings, q.stepCount)}`
    : `${levelLabel(niveau)} · ${q.stepCount} questions`;
  const shareButton =
    isPrivate && privateCode ? (
      <ArenaIconButton
        label="Partager le défi"
        onClick={() =>
          void shareContent({
            title,
            text: 'Rejoins mon défi live sur JeRaisonne !',
            url: privateLiveUrl(privateCode),
          })
        }
      >
        <Share2 className="h-5 w-5" aria-hidden />
      </ArenaIconButton>
    ) : null;

  return (
    <LiveArena coverUrl={coverUrl} tone={tone}>
      <div className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] mx-auto flex min-h-dvh w-full max-w-lg flex-col px-4 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-4">
        {/* En-tête : salon et lecture. Le jeu a sa barre figée, la fin son héros. */}
        {(q.phase === 'waiting' || q.phase === 'reading') && (
          <ArenaHeader
            onBack={goBack}
            eyebrow={isPrivate ? 'Défi privé' : 'Live School'}
            title={title}
            subtitle={subtitle}
            right={shareButton}
          />
        )}

        {/* Pendant le jeu : rappel « mode test » en haut. À la fin il est déplacé
            tout en bas de l'écran de résultat (avec le bouton Supprimer). */}
        {testMode && q.phase !== 'finished' && (
          <div className="mb-4 flex items-start gap-2 rounded-2xl bg-[rgba(244,198,78,0.12)] px-3 py-2 text-xs text-[#fadf90] ring-1 ring-[#f4c64e]/40">
            <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Mode test : tu rejoues ce défi à la demande, avec le vrai chronométrage. Ta
              participation apparaît dans le classement — pense à la supprimer à la fin.
            </span>
          </div>
        )}

        {!q.ranked && q.phase !== 'finished' && (
          <p className="text-ink-700 mb-4 rounded-2xl bg-[rgba(255,255,255,0.08)] px-3 py-2 text-xs ring-1 ring-[rgba(255,255,255,0.1)]">
            Tu joues sans classement : le classement et le lot sont réservés aux élèves de ce
            niveau.
          </p>
        )}

        {q.prize && (q.phase === 'waiting' || q.phase === 'reading') && (
          <PrizeBanner prize={q.prize} className="mb-4" />
        )}

        {q.phase === 'waiting' && isPrivate && (
          <PrivateLobby
            code={privateCode ?? ''}
            userId={userId}
            awaitingHost={q.privateInfo?.awaiting_host ?? true}
            isGuest={privateLive?.isGuest ?? false}
            countdown={<ArenaCountdown timeLeftMs={q.timeLeftMs} />}
          />
        )}

        {q.phase === 'waiting' && !isPrivate && (
          <WaitingScreen
            timeLeftMs={q.timeLeftMs}
            stepCount={q.stepCount}
            stepDurationMs={q.stepDurationMs}
            scoring={q.scoring}
            challengeId={q.challengeId}
            userId={userId}
          />
        )}

        {q.phase === 'reading' && (
          <ReadingScreen
            timeLeftMs={q.timeLeftMs}
            statement={q.commonStatement}
            stepCount={q.stepCount}
          />
        )}

        {q.phase === 'live' && (
          <LiveScreen
            level={niveau}
            challengeId={q.challengeId}
            revealedSteps={q.revealedSteps}
            commonStatement={q.commonStatement}
            stepIndex={q.stepIndex}
            stepCount={q.stepCount}
            timeLeftMs={q.timeLeftMs}
            stepDurationMs={q.stepDurationMs}
            stepLoading={q.stepLoading}
            totalScore={q.totalScore}
            eligible={q.eligible}
            ranked={q.ranked}
            unit={unit}
            stepParticipation={q.stepParticipation}
            onAnswer={q.submit}
            onClueComplete={q.registerClueComplete}
            onBack={goBack}
            notes={stepNotes}
            overallRank={myRank}
            overallTotal={myRankTotal}
            privateLive={isPrivate}
          />
        )}

        {/* Popup de fin de chasse aux indices (avant la question). Prioritaire sur
            la popup de réponse (elle arrive en amont). Feuille claire. */}
        {q.clueFeedback ? (
          <div className="theme-paper">
            <FeedbackPopup
              reward={clueReward(q.clueFeedback.xp)}
              wasCorrect
              sessionXp={q.sessionXp}
              onClose={q.dismissClueFeedback}
              titleOverride="Indices trouvés !"
              emojiOverride="🔍"
              continueLabel="Répondre"
            />
          </div>
        ) : (
          q.pendingFeedback &&
          !paper && (
            <div className="theme-paper">
              <FeedbackPopup
                reward={q.pendingFeedback.reward}
                wasCorrect={q.pendingFeedback.wasCorrect}
                sessionXp={q.sessionXp}
                onClose={q.dismissFeedback}
                extra={
                  <>
                    <StepPodium
                      challengeId={q.challengeId}
                      step={q.pendingFeedback.stepIndex ?? null}
                      unit={unit}
                    />
                    <LiveRankExtra
                      stepPosition={q.pendingFeedback.stepPosition ?? null}
                      stepTotal={q.pendingFeedback.stepTotal ?? 0}
                      overallRank={myRank}
                      overallTotal={myRankTotal}
                    />
                  </>
                }
              />
            </div>
          )
        )}

        {q.phase === 'finished' && (
          <FinishedScreen
            challengeId={q.challengeId!}
            niveau={niveau}
            userId={userId}
            title={title}
            totalScore={q.totalScore}
            correctCount={q.correctCount}
            stepCount={q.stepCount}
            eligible={q.eligible}
            ranked={q.ranked}
            unit={unit}
            prize={q.prize}
            testMode={testMode}
            privateBoard={isPrivate ? (privateBoard.data ?? { rows: [], me: null }) : undefined}
            privateCode={isPrivate ? privateCode : null}
            nationalTop={rankQuery.data?.top100 ?? []}
            myRank={myRank}
            rankTotal={myRankTotal}
            steps={q.revealedSteps}
            isGuest={privateLive?.isGuest ?? false}
            onBack={goBack}
            shareButton={shareButton}
            paper={paper}
            notes={stepNotes}
          />
        )}
      </div>
    </LiveArena>
  );
}

interface NationalBoard {
  top100?: {
    rank: number;
    student_id: string;
    display_name: string;
    avatar_url: string | null;
    total_score: number;
  }[];
  me: { rank: number | null; total: number } | null;
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-lg flex-col items-center justify-center px-4 text-center">
      {children}
    </div>
  );
}

/** Chiffres du compte à rebours (j/h/min/s). Réutilisé hors player (inscription). */
export function CountdownDigits({
  timeLeftMs,
  size = 'lg',
}: {
  timeLeftMs: number;
  size?: 'md' | 'lg';
}) {
  const segments = countdownSegments(timeLeftMs);
  const digit = size === 'lg' ? 'text-5xl sm:text-6xl' : 'text-4xl sm:text-5xl';
  return (
    <div className="flex items-end justify-center gap-2 sm:gap-4">
      {segments.map((seg) => (
        <div key={seg.label} className="flex flex-col items-center">
          <span className={`text-primary-600 font-black tabular-nums leading-none ${digit}`}>
            {seg.value.toString().padStart(2, '0')}
          </span>
          <span className="text-ink-500 mt-2 text-xs font-semibold uppercase tracking-wide">
            {seg.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Grand compte à rebours de l'arène : « 00:32 » (« 2 j » au-dessus s'il le faut). */
export function ArenaCountdown({ timeLeftMs }: { timeLeftMs: number }) {
  const { days, clock } = clockLabel(timeLeftMs);
  return (
    <div className="flex flex-col items-center" data-testid="arena-countdown">
      {days > 0 && <span className="font-display text-lg font-bold text-[#f4c64e]">{days} j</span>}
      <span className="font-display text-6xl font-black tabular-nums leading-none tracking-tight text-white drop-shadow-[0_4px_24px_rgba(47,134,255,0.55)]">
        {clock}
      </span>
    </div>
  );
}

/** Lot du live : la SKILLCARD à gagner (visuel de la carte). */
export function PrizeBanner({ prize, className = '' }: { prize: LivePrize; className?: string }) {
  return <LivePrizeTile prize={prize} tone="glass" className={className} />;
}

/**
 * Inscription à un live public, dans la salle d'attente (31/10/2026) : « Tu participes ✓ » ou « Je participe »
 * (même inscription que l'onglet Live School : rappels email et notification), et le nombre d'inscrits.
 */
function LiveParticipation({ challengeId, userId }: { challengeId: string; userId: string }) {
  const { data: mine } = useMyLiveRegistrations(userId);
  const { register, unregister, isMutating } = useLiveRegistrationMutations(userId);
  const registered = mine?.has(challengeId) ?? false;
  return (
    <ArenaPanel className="space-y-2">
      <ParticipateSwitchRow
        tone="glass"
        label={registered ? 'Tu participes ✓' : 'Je participe'}
        hint={
          registered
            ? 'Rappel email 10 min avant. Décoche pour te retirer.'
            : 'Inscris-toi pour être prévenu·e avant le coup d’envoi.'
        }
        checked={registered}
        disabled={isMutating || !mine}
        onChange={() => (registered ? unregister(challengeId) : register(challengeId))}
      />
    </ArenaPanel>
  );
}

function WaitingScreen({
  timeLeftMs,
  stepCount,
  stepDurationMs,
  scoring,
  challengeId,
  userId,
}: {
  timeLeftMs: number;
  stepCount: number;
  stepDurationMs: number;
  scoring: 'hora' | 'points';
  challengeId?: string | null;
  /** compte connecté (vide : visiteur) : peut s'inscrire */
  userId?: string;
}) {
  const perStep = Math.round(stepDurationMs / 1000);
  const { data: counts } = useLiveRegistrationCounts(challengeId ? [challengeId] : []);
  const count = challengeId ? counts?.get(challengeId) : undefined;
  return (
    <div className="space-y-4">
      <ArenaPanel className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-ink-700 text-sm font-semibold">Le défi commence dans</p>
        <ArenaCountdown timeLeftMs={timeLeftMs} />
        <p className="text-ink-500 text-xs">Reste sur cette page, ça démarre tout seul.</p>
        {count !== undefined && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(5,102,242,0.22)] px-3 py-1.5 text-sm font-semibold text-[#cfe2ff]"
            data-testid="live-registered-count"
          >
            <Users className="h-4 w-4" aria-hidden />
            {registeredLabel(count)}
          </span>
        )}
      </ArenaPanel>
      {challengeId && userId && (
        <div data-testid="live-participation">
          <LiveParticipation challengeId={challengeId} userId={userId} />
        </div>
      )}
      <ArenaInfoList
        items={[
          { icon: ListChecks, text: `${stepCount} questions · ${perStep} s chacune` },
          { icon: Users, text: 'Tout le monde démarre en même temps' },
          ...(scoring === 'hora'
            ? [
                {
                  icon: Trophy,
                  text: 'Chaque bonne réponse rapporte l’HORA de sa SKILLCARD, jusqu’à deux fois plus si tu réponds vite',
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}

/** Énoncé COMMUN (situation) : texte + visuels, sur une carte de verre. */
function CommonStatement({ blocks }: { blocks: LiveContentBlock[] }) {
  if (blocks.length === 0) return null;
  return (
    <ArenaPanel className="space-y-3">
      {blocks.map((c, i) => (
        <div key={i} className="space-y-2">
          {c.content_md && <MarkdownMath>{c.content_md}</MarkdownMath>}
          {c.visuals && c.visuals.length > 0 && (
            <VisualBlocksRenderer blocks={c.visuals as VisualBlocks} />
          )}
        </div>
      ))}
    </ArenaPanel>
  );
}

/** Phase de LECTURE : énoncé commun + compte à rebours avant les questions. */
function ReadingScreen({
  timeLeftMs,
  statement,
  stepCount,
}: {
  timeLeftMs: number;
  statement: LiveContentBlock[];
  stepCount: number;
}) {
  const seconds = Math.ceil(timeLeftMs / 1000);
  return (
    <div className="space-y-4">
      <ArenaPanel className="flex items-center justify-between gap-3 p-3 pl-4">
        <span className="font-display text-base font-bold text-white">
          {statement.length > 0 ? 'Lis bien l’énoncé…' : 'Prépare-toi…'}
        </span>
        <TimerPill seconds={seconds} />
      </ArenaPanel>
      <CommonStatement blocks={statement} />
      <p className="text-ink-500 text-center text-xs">
        Les {stepCount} questions démarrent dans {seconds} s.
      </p>
    </div>
  );
}

interface LiveScreenProps {
  /** la classe du défi : elle décide des Moves que la trousse peut proposer */
  level?: string;
  revealedSteps: RevealedStep[];
  commonStatement: LiveContentBlock[];
  stepIndex: number;
  stepCount: number;
  timeLeftMs: number;
  stepDurationMs: number;
  stepLoading: boolean;
  totalScore: number;
  eligible: boolean;
  ranked: boolean;
  /** Unité du score : « HORA » (live de programme) ou « pts ». */
  unit: string;
  /** Décompte de participation de l'étape courante (bots en test, RPC en réel). */
  stepParticipation: StepParticipation | null;
  onAnswer: (chosen: string | string[] | null) => void;
  /** Chasse aux indices terminée sur l'étape courante (XP totale glanée). */
  onClueComplete: (xp: number) => void;
  /** Défi (feuille de brouillon d'un défi privé, migration 0651). */
  challengeId: string | null;
  onBack: () => void;
  /** Sur la feuille : la récompense de chaque question (série, rang sur la question). */
  notes?: Record<number, LiveFeedback>;
  overallRank?: number | null;
  overallTotal?: number;
  /** Défi privé : bulles « avatar ±HORA » et panneau des progrès (0704). */
  privateLive?: boolean;
}

/** Médaille de podium pour les 3 premiers (sinon rien). */
function podiumMedal(pos: number): string | null {
  return pos === 1 ? '🥇' : pos === 2 ? '🥈' : pos === 3 ? '🥉' : null;
}

/** Récompense « gamy » synthétique pour la popup de fin de chasse aux indices. */
function clueReward(xp: number): XpReward {
  return {
    total: xp,
    base: xp,
    streak: 0,
    streakBonus: 0,
    timeBonus: 0,
    hasStreakBonus: false,
    hasTimeBonus: false,
  };
}

/** Podium de la question (dans la popup de réponse). Rien tant qu'il est vide. */
function StepPodium({
  challengeId,
  step,
  unit,
}: {
  challengeId: string | null;
  step: number | null;
  unit: string;
}) {
  const podium = useLiveStepPodium(challengeId, step);
  const rows = podium.data?.rows ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="mt-4">
      <p className="text-ink-500 mb-2 text-xs font-semibold uppercase tracking-wide">
        Les plus rapides
      </p>
      <LivePodium rows={rows} unit={unit} className="mt-7" />
    </div>
  );
}

/** Top 3 d'un défi privé, depuis son classement. PUR. */
export function finalPodiumRows(board: PrivateBoard | undefined): PodiumEntry[] {
  return (board?.rows ?? [])
    .filter((r) => r.rank <= 3)
    .slice(0, 3)
    .map((r) => ({
      rank: r.rank,
      name: r.name,
      avatar_url: r.avatar_url,
      is_me: r.is_me,
      time_ms: r.avg_time_ms ?? null,
      points: r.total_score,
    }));
}

/** Top 3 du classement national d'un live public. PUR. */
export function nationalPodiumRows(
  top: NonNullable<NationalBoard['top100']>,
  userId: string,
): PodiumEntry[] {
  return (
    top
      // podium : seulement les élèves qui ont marqué (un 0 n'y monte jamais)
      .filter((r) => r.rank <= 3 && r.total_score > 0)
      .slice(0, 3)
      .map((r) => ({
        rank: r.rank,
        name: (r.display_name ?? 'Élève').split(' ')[0] ?? 'Élève',
        avatar_url: r.avatar_url,
        is_me: r.student_id === userId,
        time_ms: null,
        points: r.total_score,
      }))
  );
}

/**
 * Bloc « classement » injecté dans la popup de réponse (Live School) : mon rang
 * sur CETTE question (podium) + mon rang général dans le défi.
 */
function LiveRankExtra({
  stepPosition,
  stepTotal,
  overallRank,
  overallTotal,
}: {
  stepPosition: number | null;
  stepTotal: number;
  overallRank: number | null;
  overallTotal: number;
}) {
  if (stepPosition == null && overallRank == null) return null;
  const medal = stepPosition != null ? podiumMedal(stepPosition) : null;
  return (
    <div className="mt-4 grid grid-cols-2 gap-2 text-left">
      {stepPosition != null && (
        <div className="bg-ink-50 rounded-2xl px-3 py-2">
          <p className="text-ink-500 text-xs font-semibold">Sur cette question</p>
          <p className="font-display text-primary-600 text-lg font-extrabold">
            {medal ? `${medal} ` : ''}
            {ordinalFr(stepPosition)}
            {stepTotal > 0 && (
              <span className="text-ink-400 text-sm font-semibold"> / {stepTotal}</span>
            )}
          </p>
        </div>
      )}
      {overallRank != null && (
        <div className="bg-ink-50 rounded-2xl px-3 py-2">
          <p className="text-ink-500 flex items-center gap-1 text-xs font-semibold">
            <Trophy className="h-3.5 w-3.5 shrink-0" aria-hidden /> Ton rang
          </p>
          <p className="font-display text-ink-900 text-lg font-extrabold">
            #{overallRank}
            {overallTotal > 0 && (
              <span className="text-ink-400 text-sm font-semibold"> / {overallTotal}</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

/** Chrono « ⏱ 00:18 » : vert, puis ROUGE sous les 9 dernières secondes. */
function TimerPill({ seconds }: { seconds: number }) {
  const urgent = seconds < 9;
  const s = Math.max(0, seconds);
  const label = `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  return (
    <span
      aria-label={`${s} secondes`}
      className={cn(
        'font-display inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-base font-extrabold tabular-nums',
        urgent
          ? 'bg-[#ef4444] text-white shadow-[0_0_18px_rgba(239,68,68,0.55)] motion-safe:animate-pulse'
          : 'bg-[#0b2a3f] text-[#6ee7b7] ring-2 ring-[#34d399]',
      )}
    >
      <Timer className="h-4 w-4" aria-hidden />
      {label}
    </span>
  );
}

const LIVE_NAV_ITEMS: QuickNavItem[] = [
  { id: 'live-top', label: 'Énoncé', icon: BookOpen, scrollBlock: 'start' },
  { id: 'live-current', label: 'Question', icon: HelpCircle, scrollBlock: 'center' },
];

/**
 * Écran LIVE : affichage ACCUMULÉ (comme le player d'exercice V2). Énoncé
 * (situation) + questions/réponses précédentes restent visibles ; la question
 * courante est en bas (feuille claire), seule interactive, sous une barre figée
 * (question, chrono, progression, score). Navigation rapide (haut / question /
 * calculatrice) à droite. La bonne réponse n'est révélée qu'à la FIN de l'étape
 * — au clic, seul le choix sélectionné se colore ; le résultat + la popup
 * arrivent à l'échéance.
 */
function LiveScreen(props: LiveScreenProps) {
  // la feuille v2 publie ses outils (tableau, arbre…) dans la trousse, comme dans l'exercice
  return (
    <TrousseToolsProvider>
      <LiveScreenBody {...props} />
    </TrousseToolsProvider>
  );
}

function LiveScreenBody(props: LiveScreenProps) {
  const {
    revealedSteps,
    commonStatement,
    stepIndex,
    stepCount,
    timeLeftMs,
    stepDurationMs,
    stepLoading,
    stepParticipation,
  } = props;
  const seconds = Math.ceil(timeLeftMs / 1000);
  const currentShown = revealedSteps.some((s) => s.isCurrent);
  const [showCalculator, setShowCalculator] = useState(false);
  const progress = liveProgress(stepIndex, stepCount, timeLeftMs, stepDurationMs);
  // Exercice « sur la feuille de brouillon » (drapeau `paper_player`).
  const paper = usePaperPlayerForMe();
  // Défi privé : les lignes validées des autres joueurs, en direct.
  const live = useLiveProgress(props.challengeId, !!props.privateLive);
  const progressPanel = useProgressPanelToggle();

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [stepIndex, currentShown]);

  // Scope UNIQUE de la chasse aux indices = toute la colonne accumulée (énoncé
  // commun + questions), comme le `data-clue-scope` du player V2. Le texte source
  // des indices vit dans l'énoncé commun (rendu en tête, hors de chaque step) →
  // scoper au step seul rendait les mots incliquables (bug « les indices ne
  // marchent pas »). Les overlays verts se positionnent contre ce conteneur
  // `relative`.
  const clueScopeRef = useRef<HTMLDivElement | null>(null);

  // Feuille de brouillon (défi privé, 0651) : une par question « avec feuille ».
  const scratch = useLiveScratch(props.challengeId);
  const [scratchOpen, setScratchOpen] = useState<number | null>(null);
  // Question répondue ou passée : sa feuille se relit seulement.
  useEffect(() => {
    for (const rs of revealedSteps) {
      const done = rs.revealed || rs.chosenBlockId != null || rs.chosenBlockIds.length > 0;
      if (done) scratch.lock(rs.index);
    }
    if (scratchOpen !== null && scratchOpen !== stepIndex) setScratchOpen(null);
  }, [revealedSteps, scratch, scratchOpen, stepIndex]);
  const openStep = scratchOpen !== null ? revealedSteps.find((r) => r.index === scratchOpen) : null;
  // dernière question corrigée : sa note porte le podium et le rang
  const lastNoted = revealedSteps.reduce(
    (m, r) => (r.revealed && r.result ? Math.max(m, r.index) : m),
    -1,
  );
  const openSession = openStep ? scratch.get(openStep.index, openStep.step) : null;

  return (
    <div className="flex flex-1 flex-col">
      <ExerciseQuickNav
        items={LIVE_NAV_ITEMS}
        onCalculator={() => setShowCalculator(true)}
        // sur la feuille : la trousse, au bord droit (calculatrice, énoncé, question en cours, et les
        // outils de la feuille de la question) — comme dans un exercice
        variant={paper ? 'bar' : 'float'}
      />
      {!paper && openStep && openSession && (
        <ScratchSheet
          session={openSession}
          onClose={() => setScratchOpen(null)}
          tutorialOnOpen="never"
          statement={
            <div className="text-ink-900 font-sans text-base font-medium leading-relaxed">
              <MarkdownMath>{openStep.step.prompt_md}</MarkdownMath>
            </div>
          }
        />
      )}
      {showCalculator && <CalculatorModal onClose={() => setShowCalculator(false)} />}

      {/* Barre figée : retour, numéro de question, chrono ; puis la progression
          du live et le score. Décalée sous la barre de statut en natif ; laisse
          la place de la navigation rapide à droite sur mobile. */}
      <div className="native:top-[calc(env(safe-area-inset-top)+0.5rem)] sticky top-2 z-10 mb-5 pr-12 sm:pr-0">
        <div
          className="rounded-3xl bg-[rgba(6,20,51,0.78)] p-2 shadow-[0_10px_30px_rgba(0,6,24,0.4)] ring-1 ring-[rgba(255,255,255,0.1)] backdrop-blur-md"
          data-testid="live-topbar"
        >
          <div className="flex items-center gap-2">
            <ArenaIconButton label="Quitter le live" onClick={props.onBack}>
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </ArenaIconButton>
            <span className="font-display rounded-full bg-[rgba(255,255,255,0.1)] px-3 py-1.5 text-sm font-bold text-white">
              Question {stepIndex + 1}/{stepCount}
            </span>
            <span className="flex-1" />
            {props.privateLive && (
              <ProgressToggleButton on={progressPanel.on} onToggle={progressPanel.toggle} />
            )}
            <TimerPill seconds={seconds} />
          </div>
          <div className="mt-2 flex items-center gap-3 px-1 pb-0.5">
            <div
              className="h-2 flex-1 overflow-hidden rounded-full bg-[rgba(255,255,255,0.12)]"
              role="progressbar"
              aria-label="Avancée du live"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress * 100)}
            >
              <div
                className="bg-linear-to-r h-full rounded-full from-[#0566f2] to-[#5aa9ff] transition-[width] duration-1000 ease-linear"
                style={{ width: `${Math.round(progress * 1000) / 10}%` }}
              />
            </div>
            <span className="font-display shrink-0 text-sm font-extrabold tabular-nums text-[#f4c64e]">
              {formatScore(props.totalScore)} {props.unit}
            </span>
          </div>
        </div>
      </div>

      {props.privateLive && <ProgressBubbles bubbles={live.bubbles} />}
      {props.privateLive && progressPanel.on && (
        <ProgressPanel rows={live.rows} unit={props.unit} />
      )}

      {props.ranked && !props.eligible && (
        <p className="text-ink-700 mb-3 rounded-2xl bg-[rgba(255,255,255,0.08)] px-3 py-2 text-xs ring-1 ring-[rgba(255,255,255,0.1)]">
          Tu as rejoint en cours : tes réponses comptent pour l'entraînement mais pas pour le
          classement officiel.
        </p>
      )}

      {paper ? (
        <PaperPage className="-mx-4 min-h-0 rounded-3xl">
          <div ref={clueScopeRef} data-clue-scope className="relative">
            <PaperColumn className="space-y-4 pt-4">
              <div id="live-top" className="space-y-4">
                <PaperCommonStatement blocks={commonStatement} />
              </div>
              {revealedSteps.map((rs) => (
                <Fragment key={rs.index}>
                  {rs.isCurrent ? (
                    <PaperLiveStep
                      rs={rs}
                      onAnswer={props.onAnswer}
                      onClueComplete={props.onClueComplete}
                      scopeRef={clueScopeRef}
                      seconds={seconds}
                      unit={props.unit}
                      participation={stepParticipation}
                      scratchSession={scratch.get(rs.index, rs.step)}
                      scratchV2={scratch.getV2(rs.index, rs.step)}
                      freeKey={freeSheetKey('live', props.challengeId, String(rs.index))}
                      level={props.level}
                    />
                  ) : (
                    <PaperPastStep rs={rs} unit={props.unit} />
                  )}
                  <LiveStepNote
                    rs={rs}
                    note={props.notes?.[rs.index]}
                    unit={props.unit}
                    latest={rs.index === lastNoted}
                    challengeId={props.challengeId}
                    overallRank={props.overallRank ?? null}
                    overallTotal={props.overallTotal ?? 0}
                  />
                </Fragment>
              ))}
              {!currentShown && (
                <p className="text-ink-600 py-10 text-center text-sm">
                  {stepLoading ? 'Déchiffrement de la question…' : 'En attente…'}
                </p>
              )}
              <div ref={bottomRef} />
            </PaperColumn>
          </div>
        </PaperPage>
      ) : (
        <div ref={clueScopeRef} data-clue-scope className="relative space-y-5">
          {/* Énoncé commun persistant en tête de la zone accumulée. */}
          <div id="live-top" className="space-y-5">
            <CommonStatement blocks={commonStatement} />
          </div>

          {revealedSteps.map((rs) => (
            <StepBlock
              key={rs.index}
              rs={rs}
              onAnswer={props.onAnswer}
              onClueComplete={props.onClueComplete}
              scopeRef={clueScopeRef}
              seconds={seconds}
              unit={props.unit}
              participation={rs.isCurrent ? stepParticipation : null}
              scratchSession={scratch.get(rs.index, rs.step)}
              onOpenScratch={() => setScratchOpen(rs.index)}
            />
          ))}

          {!currentShown && (
            <ArenaPanel className="text-ink-600 py-10 text-center text-sm">
              {stepLoading ? 'Déchiffrement de la question…' : 'En attente…'}
            </ArenaPanel>
          )}
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  );
}

/** Contexte et énoncé d'une question (intro, sous-énoncé, consigne, visuels). */
function StepStatement({ rs, big }: { rs: RevealedStep; big: boolean }) {
  const { step } = rs;
  const intro = step.intro ?? [];
  const promptVisuals = (step.prompt_visuals ?? []) as VisualBlocks;
  return (
    <div className="space-y-3">
      {intro.map((c, i) => (
        <div key={i} className="space-y-2">
          {c.content_md && <MarkdownMath>{c.content_md}</MarkdownMath>}
          {c.visuals && c.visuals.length > 0 && (
            <VisualBlocksRenderer blocks={c.visuals as VisualBlocks} />
          )}
        </div>
      ))}
      {step.sub_statement_md && (
        <div className="text-ink-700">
          <MarkdownMath>{step.sub_statement_md}</MarkdownMath>
        </div>
      )}
      <div
        className={cn(
          big ? 'font-display text-ink-900 text-lg font-bold leading-snug' : 'font-medium',
        )}
      >
        <MarkdownMath>{step.prompt_md}</MarkdownMath>
      </div>
      {promptVisuals.length > 0 && <VisualBlocksRenderer blocks={promptVisuals} />}
    </div>
  );
}

/** Corrigé d'une question résolue : la ou les bonnes réponses + les points gagnés. */
function StepCorrection({ rs, unit }: { rs: RevealedStep; unit: string }) {
  const { step, result } = rs;
  const correctIds =
    result?.correctBlockIds ?? (result?.correctBlockId ? [result.correctBlockId] : []);
  const correctChoices = step.choices.filter((c) => correctIds.includes(c.id));
  const points = result?.points ?? 0;
  return (
    <div className="flex items-center gap-3">
      <div className="text-ink-800 flex-1 space-y-1 text-sm">
        {correctChoices.length > 0 ? (
          correctChoices.map((c) => (
            <div key={c.id} className="flex items-start gap-1.5">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#34d399]" aria-hidden />
              <MarkdownMath>{c.label_md}</MarkdownMath>
            </div>
          ))
        ) : (
          <span className="text-ink-400 text-xs italic">Correction…</span>
        )}
      </div>
      <span
        className={cn(
          'font-display shrink-0 rounded-full px-2.5 py-1 text-sm font-extrabold tabular-nums',
          points > 0 ? 'bg-[rgba(52,211,153,0.15)] text-[#6ee7b7]' : 'text-ink-500',
        )}
      >
        +{points} {unit}
      </span>
    </div>
  );
}

/**
 * Rendu d'UNE étape accumulée.
 * - Étape PASSÉE : carte de verre compacte (question + corrigé + points).
 * - Étape COURANTE : la question sur une feuille claire (gain en HORA en
 *   pastille dorée), puis trois états :
 *   1. NON répondue (interactif) → choix cliquables ;
 *   2. RÉPONDUE, non résolue → seul MON choix + carte d'attente (« tu es le Nᵉ ») ;
 *   3. RÉSOLUE en avance → corrigé + « Prochaine question dans Xs ».
 */
function StepBlock({
  rs,
  onAnswer,
  onClueComplete,
  scopeRef,
  seconds,
  unit,
  participation,
  scratchSession = null,
  onOpenScratch,
}: {
  rs: RevealedStep;
  /** Unité du score affiché (« HORA » ou « pts »). */
  unit: string;
  onAnswer: (chosen: string | string[] | null) => void;
  /** Chasse aux indices terminée (XP glané) → popup + déblocage des choix. */
  onClueComplete: (xp: number) => void;
  /** Scope PARTAGÉ de la chasse aux indices (toute la colonne, cf. LiveScreen). */
  scopeRef: React.RefObject<HTMLDivElement | null>;
  /** Secondes restantes de l'étape courante. */
  seconds: number;
  /** Décompte de participation — non-null uniquement pour l'étape courante. */
  participation: StepParticipation | null;
  /** Feuille de brouillon de la question (défi privé), sinon null. */
  scratchSession?: ScratchSession | null;
  onOpenScratch?: () => void;
}) {
  const { step, chosenBlockId, chosenBlockIds, isCurrent, revealed } = rs;
  const multiple = step.answer_mode === 'multiple';
  const answered = chosenBlockId != null || chosenBlockIds.length > 0;
  // Sélection LOCALE en mode multiple (avant validation).
  const [localSel, setLocalSel] = useState<string[]>([]);
  const chosenSet = answered ? new Set(chosenBlockIds) : new Set(localSel);
  const clueHunt = step.clue_hunt;
  // Chasse aux indices : tant qu'elle n'est pas terminée, les choix sont GÂTÉS
  // (« trouve d'abord les indices »). À la complétion → popup + déblocage. Une
  // chasse sans indice (dégénérée) ne gâte jamais.
  const [clueDone, setClueDone] = useState(false);
  const clueGate = !!clueHunt && clueHunt.clues.length > 0 && !clueDone;
  // Mes choix (pour l'état « répondu, en attente » : on ne montre que ça).
  const myChoices = step.choices.filter((c) => chosenSet.has(c.id));
  const remaining = participation
    ? Math.max(0, participation.total - participation.answered)
    : null;

  // Chasse aux indices attachée à la question (payload live v2+). En step
  // revealed (passé), disabled=true → mode read-only.
  const clueHuntNode = clueHunt ? (
    <ClueHuntLive
      data={clueHunt}
      disabled={revealed || answered}
      scopeRef={scopeRef}
      onComplete={(xp) => {
        setClueDone(true);
        onClueComplete(xp);
      }}
    />
  ) : null;
  const scratchNode =
    scratchSession && onOpenScratch ? (
      <ScratchEntryCard
        session={scratchSession}
        answered={answered || revealed}
        onOpen={onOpenScratch}
      />
    ) : null;

  // ── Étape passée : carte compacte ──────────────────────────────────────────
  if (!isCurrent) {
    return (
      <ArenaPanel className="space-y-3 opacity-90">
        <p className="font-display text-ink-500 text-xs font-bold uppercase tracking-wider">
          Question {rs.index + 1}
        </p>
        <StepStatement rs={rs} big={false} />
        {scratchNode}
        {clueHuntNode}
        {revealed && <StepCorrection rs={rs} unit={unit} />}
      </ArenaPanel>
    );
  }

  // ── Étape courante ─────────────────────────────────────────────────────────
  return (
    <div id="live-current" className="space-y-3 pt-2">
      <div className="relative">
        {step.card && step.card.hora_base > 0 && (
          <span
            title="Jusqu’à deux fois plus si tu réponds vite"
            className="font-display absolute -top-3 right-4 z-10 rounded-full bg-[#f4c64e] px-3 py-1 text-sm font-extrabold text-[#0a2e57] shadow-[0_6px_16px_rgba(244,198,78,0.45)]"
          >
            +{step.card.hora_base} HORA
          </span>
        )}
        <div
          className="theme-paper rounded-3xl bg-white p-5 shadow-[0_18px_40px_rgba(0,6,24,0.45)]"
          data-testid="live-question-card"
        >
          <StepStatement rs={rs} big />
        </div>
      </div>

      {scratchNode}
      {clueHuntNode}

      {/* Étape courante RÉSOLUE en avance (tout le monde a répondu) : corrigé, et
          la question suivante reste gated par l'horloge → indicateur d'attente. */}
      {revealed && (
        <>
          <ArenaPanel>
            <StepCorrection rs={rs} unit={unit} />
          </ArenaPanel>
          <div className="text-ink-500 flex items-center justify-center gap-1.5 py-1 text-xs font-medium">
            <Timer className="h-3.5 w-3.5" />
            Prochaine question dans {seconds}s
          </div>
        </>
      )}

      {/* Étape courante RÉPONDUE, en attente de résolution : on masque les autres
          choix, on ne garde QUE mon choix + une carte de statut. */}
      {!revealed && answered && (
        <div className="space-y-2.5">
          {myChoices.map((choice, i) => (
            <ChoiceButton
              key={choice.id}
              letter={LETTERS[step.choices.indexOf(choice)] ?? LETTERS[i] ?? '?'}
              choice={choice}
              chosen
              multiple={multiple}
              disabled
              onClick={() => {}}
            />
          ))}
          <ArenaPanel className="space-y-1.5 p-3.5">
            <div className="flex items-center justify-between gap-2">
              <span className="font-display flex items-center gap-1.5 text-sm font-bold text-white">
                <CheckCircle2 className="h-4 w-4 text-[#34d399]" aria-hidden />
                Réponse envoyée
              </span>
              <span className="text-ink-500 text-xs">Résultat dans {seconds}s</span>
            </div>
            {participation ? (
              <div className="text-ink-600 flex items-start gap-1.5 text-xs">
                <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {participation.myPosition != null && (
                    <>
                      Tu es le{' '}
                      <strong className="text-[#f4c64e]">
                        {ordinalFr(participation.myPosition)}
                      </strong>{' '}
                      à répondre
                      {remaining && remaining > 0 ? ' · ' : ''}
                    </>
                  )}
                  {remaining != null &&
                    (remaining > 0 ? (
                      <>
                        <strong className="text-white">{remaining}</strong> en attente
                      </>
                    ) : (
                      <>tout le monde a répondu&nbsp;!</>
                    ))}
                </span>
              </div>
            ) : (
              <p className="text-ink-500 text-xs">En attente des autres…</p>
            )}
          </ArenaPanel>
        </div>
      )}

      {/* Étape courante NON répondue : QCM interactif. Les choix sont GÂTÉS tant
          que la chasse aux indices n'est pas terminée. */}
      {!revealed && !answered && (
        <div className="space-y-2.5">
          <p className="text-ink-500 text-xs font-semibold uppercase tracking-wider">
            {multiple ? 'Plusieurs réponses possibles' : 'Réponse unique'}
          </p>
          {clueGate && (
            <p className="text-center text-xs font-semibold text-cyan-300">
              🔍 Trouve d&apos;abord tous les indices pour répondre.
            </p>
          )}
          <div className={clueGate ? 'pointer-events-none space-y-2.5 opacity-40' : 'space-y-2.5'}>
            {step.choices.map((choice, i) => (
              <ChoiceButton
                key={choice.id}
                letter={LETTERS[i] ?? '?'}
                choice={choice}
                chosen={chosenSet.has(choice.id)}
                multiple={multiple}
                disabled={clueGate}
                onClick={() => {
                  if (clueGate) return;
                  if (multiple) {
                    setLocalSel((prev) =>
                      prev.includes(choice.id)
                        ? prev.filter((x) => x !== choice.id)
                        : [...prev, choice.id],
                    );
                  } else {
                    onAnswer(choice.id);
                  }
                }}
              />
            ))}
            {/* Bouton « Valider » — mode multiple, avant réponse. */}
            {multiple && (
              <button
                type="button"
                disabled={localSel.length === 0 || clueGate}
                onClick={() => onAnswer(localSel)}
                className={liveCtaClass('primary', 'mt-1')}
              >
                <Send className="h-5 w-5" aria-hidden />
                Valider ma réponse
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Exercice « sur la feuille de brouillon » (drapeau `paper_player`) ────────
// La colonne du live est une page-papier (tokens sombres dans l'arène) ; la
// question courante y est écrite, ses choix sont des touches dans le dock du bas
// (sélection puis « Check » → même envoi), le brouillon s'écrit sous la question.

/** Énoncé commun, écrit sur la feuille (sans carte de verre). */
function PaperCommonStatement({ blocks }: { blocks: LiveContentBlock[] }) {
  if (blocks.length === 0) return null;
  return (
    <div className="space-y-3 text-[15px] leading-relaxed">
      {blocks.map((c, i) => (
        <div key={i} className="space-y-2">
          {c.content_md && <MarkdownMath>{c.content_md}</MarkdownMath>}
          {c.visuals && c.visuals.length > 0 && (
            <div className="pp-figure">
              <VisualBlocksRenderer blocks={c.visuals as VisualBlocks} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Question passée : écrite, estompée, avec son corrigé. */
function PaperPastStep({ rs, unit }: { rs: RevealedStep; unit: string }) {
  return (
    <div className="space-y-2 opacity-70">
      <p className="font-display text-ink-500 text-xs font-bold uppercase tracking-wider">
        Question {rs.index + 1}
      </p>
      <StepStatement rs={rs} big={false} />
      {rs.revealed && <StepCorrection rs={rs} unit={unit} />}
    </div>
  );
}

/** Réponse laissée vide (chrono écoulé sans réponse). PUR. */
export function isBlankStep(rs: RevealedStep): boolean {
  return (
    rs.chosenBlockId == null &&
    rs.chosenBlockIds.length === 0 &&
    !rs.result?.chosenBlockId &&
    (rs.result?.chosenBlockIds.length ?? 0) === 0
  );
}

/** La correction du prof sous une question du live (sur la feuille). */
function LiveStepNote({
  rs,
  note,
  unit,
  latest,
  challengeId,
  overallRank,
  overallTotal,
}: {
  rs: RevealedStep;
  note?: LiveFeedback;
  unit: string;
  latest: boolean;
  challengeId: string | null;
  overallRank: number | null;
  overallTotal: number;
}) {
  if (!rs.revealed || !rs.result) return null;
  const blank = isBlankStep(rs);
  return (
    <TeacherNote
      wasCorrect={rs.result.wasCorrect}
      remark={blank ? 'Pas de réponse' : undefined}
      streak={note?.reward.streak}
      gain={rs.result.points}
      unit={unit}
      past={!latest}
      extra={
        latest && note ? (
          <>
            <StepPodium challengeId={challengeId} step={rs.index} unit={unit} />
            <LiveRankExtra
              stepPosition={note.stepPosition ?? null}
              stepTotal={note.stepTotal ?? 0}
              overallRank={overallRank}
              overallTotal={overallTotal}
            />
          </>
        ) : undefined
      }
    />
  );
}

/** Lignes du classement écrit sur la feuille (défi privé ou national). PUR. */
export function liveHandRankingRows(
  privateBoard: PrivateBoard | undefined,
  nationalTop: NonNullable<NationalBoard['top100']>,
  userId: string,
): HandRankingRow[] {
  if (privateBoard) {
    return privateBoard.rows.map((r) => ({
      rank: r.rank,
      name: r.name,
      score: r.total_score,
      isMe: r.is_me,
      avatarUrl: r.avatar_url,
    }));
  }
  return nationalTop.map((r) => ({
    rank: r.rank,
    name: (r.display_name ?? 'Élève').split(' ')[0] ?? 'Élève',
    score: r.total_score,
    isMe: r.student_id === userId,
    avatarUrl: r.avatar_url,
  }));
}

function LivePaletteInDock({ session, onDone }: { session: ScratchSession; onDone: () => void }) {
  const state = useSessionState(session);
  return <SheetPalette state={state} session={session} onClose={onDone} />;
}

/** Ids corrects d'une étape résolue (vide tant qu'elle ne l'est pas). */
export function liveCorrectIds(rs: RevealedStep): string[] {
  const r = rs.result;
  if (!rs.revealed || !r) return [];
  return r.correctBlockIds ?? (r.correctBlockId ? [r.correctBlockId] : []);
}

/**
 * Touches d'une question live : avant la réponse, sélection locale ; après la
 * réponse (optimiste), mon choix seul en attente de la résolution ; résolue,
 * bonnes en vert et la mienne fausse en rouge.
 */
export function liveStepQcm(
  rs: RevealedStep,
  pick: string[],
  setPick: (ids: string[]) => void,
  onCheck: (ids: string[]) => void,
  gated: boolean,
): PaperQcm {
  const { step, chosenBlockId, chosenBlockIds } = rs;
  const multiple = step.answer_mode === 'multiple';
  const answered = chosenBlockId != null || chosenBlockIds.length > 0;
  const mine = chosenBlockIds.length > 0 ? chosenBlockIds : chosenBlockId ? [chosenBlockId] : [];
  const correct = new Set(liveCorrectIds(rs));
  const options = step.choices.map((c) => ({
    id: c.id,
    label: c.label_md,
    visuals: [],
    is_correct: correct.has(c.id),
  }));
  return {
    options,
    multiple,
    layout: choiceKeyLayout(options.map((o) => ({ id: o.id, label: o.label, hasVisuals: false }))),
    selected: answered ? mine : pick,
    chosen: mine,
    revealed: rs.revealed,
    shown: true,
    busy: false,
    toggle: (id) => {
      if (answered || rs.revealed || gated) return;
      setPick(toggleChoice(pick, id, multiple));
    },
    check: () => {
      if (answered || rs.revealed || gated || pick.length === 0) return;
      onCheck(pick);
    },
  };
}

/** Exporté pour les tests. */
export function PaperLiveStep({
  rs,
  onAnswer,
  onClueComplete,
  scopeRef,
  seconds,
  unit,
  participation,
  scratchSession,
  scratchV2 = null,
  freeKey,
  level,
}: {
  rs: RevealedStep;
  onAnswer: (chosen: string | string[] | null) => void;
  onClueComplete: (xp: number) => void;
  scopeRef: React.RefObject<HTMLDivElement | null>;
  seconds: number;
  unit: string;
  participation: StepParticipation | null;
  /** feuille publiée de la question (défi privé), sinon null → feuille libre */
  scratchSession: ScratchSession | null;
  /** feuille publiée du moteur v2 : jouée avec l'interface du prototype */
  scratchV2?: LiveV2Sheet | null;
  freeKey: string | null;
  /** la classe du défi (Moves de la trousse) */
  level?: string;
}) {
  const { step, chosenBlockId, chosenBlockIds, revealed } = rs;
  const multiple = step.answer_mode === 'multiple';
  const answered = chosenBlockId != null || chosenBlockIds.length > 0;
  const [pick, setPick] = useState<string[]>([]);
  const [writing, setWriting] = useState(false);
  const [clueDone, setClueDone] = useState(false);
  const clueHunt = step.clue_hunt;
  const clueGate = !!clueHunt && clueHunt.clues.length > 0 && !clueDone;

  const free = useFreeSheet({
    questionKey: `live-${rs.index}`,
    numbers: numbersInText(step.prompt_md),
    storageKey: freeKey,
    locked: answered || revealed,
  });
  const session = scratchSession ?? free;
  const sheetWritable = !answered && !revealed && !session.getSnapshot().locked;
  // une feuille publiée qui rapporte des HORA s'ouvre d'elle-même (une fois par question)
  const hasHora = sheetOpensByDefault(scratchSession);
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || !hasHora || !sheetWritable) return;
    autoOpened.current = true;
    setWriting(true);
  }, [hasHora, sheetWritable]);
  // pastilles ✓ / ✗ des touches : ce que le brouillon établit (feuille publiée)
  const draftFound = useFoundChoices(scratchSession ?? null);
  const draftCrossed = useCrossedChoices(scratchSession ?? null);

  const qcm = liveStepQcm(
    rs,
    pick,
    setPick,
    (ids) => {
      setWriting(false);
      onAnswer(multiple ? ids : ids[0]!);
    },
    clueGate,
  );
  // sans feuille publiée : la feuille libre du moteur v2 (construite à partir de l'énoncé et des propositions)
  const optionLabels = qcm.options.map((o) => o.label).join('\u0000');
  const freeInput = useMemo(
    () => ({ promptMd: step.prompt_md, choices: optionLabels.split('\u0000'), level: null }),
    [step.prompt_md, optionLabels],
  );
  const v2 = useFreeV2Sheet({
    questionKey: `live-${rs.index}`,
    input: freeInput,
    storageKey: freeKey,
    locked: answered || revealed,
    enabled: !scratchSession,
  });
  // les Moves : le même comportement que dans un exercice (`usePlayerMoves`)
  const { user } = useAuth();
  const sheetV2 = scratchV2 ?? (v2 && !scratchSession ? v2 : null);
  const moves = usePlayerMoves({
    enabled: !!sheetV2 && !answered && !revealed,
    level,
    model: scratchSession?.model ?? null,
    session: sheetV2?.session ?? null,
    ui: sheetV2?.ui ?? null,
    // une session anonyme (invité du défi) garde ses Moves sur l’appareil
    userId: user && !user.is_anonymous ? user.id : null,
  });
  const mode = dockMode({
    hasCheckpoint: true,
    answerMode: multiple ? 'multiple' : 'single',
    answered: answered || revealed,
    clueHuntPending: clueGate,
    canAdvance: false,
    sheetWritable,
    writing,
    movePanel: !!moves.panel,
  });
  const remaining = participation
    ? Math.max(0, participation.total - participation.answered)
    : null;

  return (
    <div id="live-current" className="space-y-3 pt-1" data-testid="live-paper-step">
      <div className="flex items-start gap-2">
        <p className="font-display text-ink-500 flex-1 text-xs font-bold uppercase tracking-wider">
          Question {rs.index + 1}
        </p>
        {step.card && step.card.hora_base > 0 && (
          <span
            title="Jusqu’à deux fois plus si tu réponds vite"
            className="font-display rounded-full bg-[#f4c64e] px-2.5 py-0.5 text-xs font-extrabold text-[#0a2e57]"
          >
            +{step.card.hora_base} HORA
          </span>
        )}
      </div>
      <StepStatement rs={rs} big />
      {/* HORA GLOBALES d'un Move débloqué : jamais comptées dans le défi */}
      <HoraRewardPop reward={moves.reward} onDone={moves.clearReward} />
      {clueHunt && (
        <ClueHuntLive
          data={clueHunt}
          disabled={revealed || answered}
          scopeRef={scopeRef}
          onComplete={(xp) => {
            setClueDone(true);
            onClueComplete(xp);
          }}
        />
      )}
      <PaperChoiceList qcm={qcm} />
      {scratchV2 ? (
        <scratchV2.ui.V2SheetView
          session={scratchV2.session}
          onActivate={() => setWriting(true)}
          hideTools={moves.hiddenKeys}
          onLockedGesture={moves.onLockedGesture}
        />
      ) : v2 && !scratchSession ? (
        <v2.ui.V2SheetView
          session={v2.session}
          onActivate={() => setWriting(true)}
          hideTools={moves.hiddenKeys}
          onLockedGesture={moves.onLockedGesture}
        />
      ) : (
        <InlineSheet session={session} writing={writing} onActivate={() => setWriting(true)} />
      )}
      {answered && !revealed && <TeacherWaitingNote copies={participation?.answered ?? null} />}
      {revealed && <StepCorrection rs={rs} unit={unit} />}

      {revealed ? (
        // réponse révélée : plus de clavier ni de touches, seulement le temps avant la suivante
        <AnswerDock label="Prochaine question">
          <p
            className="flex items-center justify-center gap-1.5 py-2 text-sm text-[var(--sc-muted)]"
            data-testid="live-paper-next"
          >
            <Timer className="h-4 w-4" aria-hidden />
            Prochaine question dans {seconds}s
          </p>
        </AnswerDock>
      ) : (
        <AnswerDock label={mode === 'write' ? 'Feuille de brouillon' : 'Ta réponse'}>
          {mode === 'move' && moves.panel ? (
            <MovePanel
              shelf={moves.panel.shelf}
              state={moves.panel.state}
              onState={moves.setPanelState}
              unlocked={moves.unlocked}
              onUnlock={moves.remember}
              onApply={(m) => {
                moves.closePanel();
                moves.remember(m.id);
                // une connaissance pose sa ligne ; un geste ouvre ses touches
                if (m.kind === 'savoir' && m.writes && sheetV2) sheetV2.session.writeLine(m.writes);
                setWriting(true);
              }}
              onClose={moves.closePanel}
            />
          ) : mode === 'write' ? (
            sheetV2 ? (
              <>
                {moves.needed && <MoveNudge move={moves.needed} />}
                <sheetV2.ui.V2Keyboard
                  session={sheetV2.session}
                  onAnswer={() => setWriting(false)}
                  answerLabel="Répondre"
                  hide={moves.hiddenKeys}
                />
              </>
            ) : (
              <LivePaletteInDock session={session} onDone={() => setWriting(false)} />
            )
          ) : mode === 'clue' ? (
            <p className="font-display py-3 text-center text-sm font-semibold text-cyan-300">
              🔍 Trouve d&apos;abord tous les indices pour répondre.
            </p>
          ) : (
            <>
              {answered && !revealed ? (
                // répondue, en attente de la résolution : mon choix seul, figé
                <div className="space-y-2 py-1" data-testid="live-paper-waiting">
                  <div className="flex flex-wrap gap-2">
                    {qcm.options
                      .filter((o) => qcm.chosen.includes(o.id))
                      .map((o) => (
                        <span key={o.id} className="pp-key" data-state="selected">
                          <span className="pp-key-letter">
                            {LETTERS[qcm.options.indexOf(o)] ?? '?'}
                          </span>
                          <span className="min-w-0 truncate text-[15px]">
                            <MarkdownMath>{o.label}</MarkdownMath>
                          </span>
                        </span>
                      ))}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="font-display flex items-center gap-1.5 font-bold text-[var(--sc-good-ink)]">
                      <CheckCircle2 className="h-4 w-4" aria-hidden />
                      Réponse envoyée
                      {participation?.myPosition != null && (
                        <span className="font-normal text-[var(--sc-muted)]">
                          · {ordinalFr(participation.myPosition)}
                          {remaining && remaining > 0 ? ` · ${remaining} en attente` : ''}
                        </span>
                      )}
                    </span>
                    <span className="text-[var(--sc-muted)]">Résultat dans {seconds}s</span>
                  </div>
                </div>
              ) : (
                <AnswerKeys
                  qcm={qcm}
                  found={draftFound}
                  crossed={draftCrossed}
                  onWrite={sheetWritable ? () => setWriting(true) : undefined}
                />
              )}
            </>
          )}
        </AnswerDock>
      )}
    </div>
  );
}

function ChoiceButton({
  letter,
  choice,
  chosen,
  multiple = false,
  disabled,
  onClick,
}: {
  letter: string;
  choice: LiveChoice;
  /** Ce choix a été sélectionné par l'élève → coloration immédiate. */
  chosen: boolean;
  /** QCM à réponses multiples : pastille = case à cocher. */
  multiple?: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={chosen}
      onClick={onClick}
      className={cn(
        'flex min-h-14 w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition active:scale-[0.99] disabled:cursor-default',
        chosen
          ? 'bg-linear-to-b from-[#2f86ff] to-[#0566f2] text-white shadow-[0_8px_22px_rgba(5,102,242,0.4)]'
          : 'text-ink-900 bg-[rgba(255,255,255,0.07)] ring-1 ring-[rgba(255,255,255,0.15)] hover:bg-[rgba(255,255,255,0.11)]',
      )}
    >
      {multiple ? (
        <span
          aria-hidden
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm font-bold',
            chosen ? 'bg-[#ffffff] text-[#0566f2]' : 'ring-2 ring-[rgba(255,255,255,0.3)]',
          )}
        >
          {chosen ? '✓' : ''}
        </span>
      ) : (
        <span
          aria-hidden
          className={cn(
            'font-display w-6 shrink-0 text-base font-black',
            chosen ? 'text-white' : 'text-[#9cc4f8]',
          )}
        >
          {letter}.
        </span>
      )}
      <span className="flex-1 text-base font-semibold">
        <MarkdownMath>{choice.label_md}</MarkdownMath>
      </span>
    </button>
  );
}

/** Correction de chaque question, à la fin du live. */
function CorrectionsList({ steps, unit }: { steps: RevealedStep[]; unit: string }) {
  if (steps.length === 0) {
    return <p className="text-ink-500 text-center text-sm">Aucune question à revoir.</p>;
  }
  return (
    <ol className="space-y-3" data-testid="live-corrections">
      {steps.map((rs) => {
        const r = rs.result;
        const chosen = r
          ? r.chosenBlockIds.length > 0
            ? r.chosenBlockIds
            : r.chosenBlockId
              ? [r.chosenBlockId]
              : []
          : rs.chosenBlockIds.length > 0
            ? rs.chosenBlockIds
            : rs.chosenBlockId
              ? [rs.chosenBlockId]
              : [];
        const status = !r
          ? 'unknown'
          : r.wasCorrect
            ? 'correct'
            : chosen.length === 0
              ? 'blank'
              : 'wrong';
        const mine = rs.step.choices.filter((c) => chosen.includes(c.id));
        return (
          <li key={rs.index}>
            <ArenaPanel className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-display text-ink-500 text-xs font-bold uppercase tracking-wider">
                  Question {rs.index + 1}
                </span>
                <CorrectionStatus status={status} />
              </div>
              <StepStatement rs={rs} big={false} />
              {status === 'wrong' && mine.length > 0 && (
                <div className="text-ink-600 space-y-1 text-sm">
                  <p className="text-xs font-semibold">Ta réponse</p>
                  {mine.map((c) => (
                    <div key={c.id} className="flex items-start gap-1.5">
                      <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#f87171]" aria-hidden />
                      <MarkdownMath>{c.label_md}</MarkdownMath>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <p className="text-ink-600 mb-1 text-xs font-semibold">Bonne réponse</p>
                <StepCorrection rs={{ ...rs, revealed: true }} unit={unit} />
              </div>
            </ArenaPanel>
          </li>
        );
      })}
    </ol>
  );
}

function CorrectionStatus({ status }: { status: 'correct' | 'wrong' | 'blank' | 'unknown' }) {
  if (status === 'correct')
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(52,211,153,0.15)] px-2.5 py-1 text-xs font-bold text-[#6ee7b7]">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Juste
      </span>
    );
  if (status === 'wrong')
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(248,113,113,0.15)] px-2.5 py-1 text-xs font-bold text-[#fca5a5]">
        <XCircle className="h-3.5 w-3.5" aria-hidden /> Faux
      </span>
    );
  if (status === 'blank')
    return (
      <span className="text-ink-500 rounded-full bg-[rgba(255,255,255,0.08)] px-2.5 py-1 text-xs font-bold">
        Sans réponse
      </span>
    );
  return null;
}

/** Trait de pinceau sous « GG ! » (bleu → or). */
function Swoosh() {
  return (
    <svg viewBox="0 0 220 26" aria-hidden className="-mt-2 h-6 w-44">
      <defs>
        <linearGradient id="live-swoosh" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#2f86ff" />
          <stop offset="1" stopColor="#f4c64e" />
        </linearGradient>
      </defs>
      <path
        d="M6 18 C 70 4, 150 4, 214 13"
        fill="none"
        stroke="url(#live-swoosh)"
        strokeWidth="7"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Podium de fin (top 3) posé sur l'arène, au-dessus de la copie. Rien sans classement. */
export function FinishPodium({
  rows,
  unit,
  isPrivate,
  label,
}: {
  rows: PodiumEntry[];
  unit: string;
  isPrivate: boolean;
  label: string;
}) {
  if (rows.length === 0) return null;
  return (
    <section aria-labelledby="live-finish-podium" className="mt-2 text-center">
      <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-[#f4c64e]">
        {label}
      </p>
      <h2 id="live-finish-podium" className="font-display mt-1 text-xl font-extrabold text-white">
        Le podium
      </h2>
      <LivePodium
        rows={rows}
        unit={unit}
        size="lg"
        timeLabel={isPrivate ? 'moy.' : undefined}
        className="mt-12"
      />
    </section>
  );
}

function FinishedScreen({
  challengeId,
  niveau,
  userId,
  title,
  totalScore,
  correctCount,
  stepCount,
  eligible,
  ranked,
  unit,
  prize,
  testMode,
  privateBoard,
  privateCode,
  nationalTop,
  myRank,
  rankTotal,
  steps,
  isGuest = false,
  onBack,
  shareButton,
  paper = false,
  notes = {},
}: {
  challengeId: string;
  niveau: string;
  userId: string;
  title: string;
  totalScore: number;
  correctCount: number;
  stepCount: number;
  eligible: boolean;
  ranked: boolean;
  unit: string;
  prize: LivePrize | null;
  testMode: boolean;
  /** Défi privé (0650) : son classement remplace les classements Live School. */
  privateBoard?: PrivateBoard;
  /** Défi privé : son code (revanche). */
  privateCode: string | null;
  /** Live public : haut du classement national (podium). */
  nationalTop: NonNullable<NationalBoard['top100']>;
  myRank: number | null;
  rankTotal: number;
  /** Questions jouées (corrections). */
  steps: RevealedStep[];
  /** Invité anonyme : rien de durable, invitation à créer un compte. */
  isGuest?: boolean;
  onBack: () => void;
  shareButton: ReactNode;
  /** Sur la feuille : la copie corrigée, la note du prof et le classement écrits à la main. */
  paper?: boolean;
  notes?: Record<number, LiveFeedback>;
}) {
  const deleteTest = useDeleteTestParticipation(userId);
  const isPrivate = !!privateBoard;
  const [showCorrections, setShowCorrections] = useState(false);
  const liveLabel = isPrivate ? 'Défi privé' : 'Live School';
  const headline = finishHeadline({
    rank: myRank,
    total: rankTotal,
    correct: correctCount,
    count: stepCount,
  });
  const pct = stepCount > 0 ? Math.round((correctCount / stepCount) * 100) : 0;
  const podium = isPrivate
    ? finalPodiumRows(privateBoard)
    : nationalPodiumRows(nationalTop, userId);
  const xpEarned = !isGuest && isPrivate ? correctCount * 10 : 0;

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center justify-between gap-2">
        <ArenaIconButton label="Retour" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </ArenaIconButton>
        {shareButton}
      </div>

      {paper ? (
        <>
          {/* Le podium du top 3 (en HORA) d'abord, puis la copie corrigée et la note. */}
          <FinishPodium
            rows={podium}
            unit={unit}
            isPrivate={isPrivate}
            label={`${liveLabel} · ${levelLabel(niveau)}`}
          />
          <PaperPage className="-mx-4 mt-3 min-h-0 rounded-3xl" data-testid="live-finish-paper">
            <PaperColumn className="space-y-4 pb-6 pt-4">
              <div>
                <p className="font-display text-primary-600 text-xs font-bold uppercase tracking-[0.18em]">
                  {liveLabel} · {levelLabel(niveau)}
                </p>
                <h1
                  className="font-display text-ink-900 text-lg font-bold"
                  data-testid="live-finish-title"
                >
                  {title}
                </h1>
              </div>
              {steps.map((rs) => (
                <Fragment key={rs.index}>
                  <PaperPastStep rs={rs} unit={unit} />
                  <LiveStepNote
                    rs={rs}
                    note={notes[rs.index]}
                    unit={unit}
                    latest={false}
                    challengeId={challengeId}
                    overallRank={myRank}
                    overallTotal={rankTotal}
                  />
                </Fragment>
              ))}
              <FinalTeacherGrade
                correct={correctCount}
                total={stepCount}
                grade={gradeOn20(correctCount, stepCount)}
                remark={headline.title}
              >
                <ul className="pp-teacher-small space-y-0.5" data-testid="live-finish-details">
                  <li>
                    +{formatScore(totalScore)} {unit}
                    {xpEarned > 0 ? ` · +${xpEarned} XP` : ''}
                  </li>
                  {/* « Tu termines 2e sur 5 » (une seule ligne : avant, « 2e sur 2 » puis « Tu termines 2e ») */}
                  <li>
                    {myRank != null
                      ? `${headline.line1}${rankTotal > 1 ? ` sur ${rankTotal}` : ''}`
                      : headline.line1}
                  </li>
                  <li>{headline.praise}</li>
                </ul>
              </FinalTeacherGrade>
              <HandRanking
                rows={liveHandRankingRows(privateBoard, nationalTop, userId)}
                unit={unit}
                title={isPrivate ? 'Classement du défi' : 'Classement'}
              />
              <FinishNotes
                ranked={ranked}
                eligible={eligible}
                isGuest={isGuest}
                isPrivate={isPrivate}
                correctCount={correctCount}
                totalScore={totalScore}
                unit={unit}
                inPrizeZone={
                  !!prize && ranked && eligible && myRank != null && myRank <= prize.top_n
                }
                prizeTopN={prize?.top_n ?? 0}
              />
            </PaperColumn>
          </PaperPage>
          {prize && <PrizeBanner prize={prize} className="mt-4" />}

          {/* Actions. */}
          <div className="mt-5 flex flex-col gap-3">
            {isPrivate && !isGuest && privateCode && (
              <Link
                to="/live/prive/nouveau"
                search={{ from: privateCode }}
                className={liveCtaClass('primary')}
              >
                <RefreshCw className="h-5 w-5" aria-hidden />
                Prendre ma revanche
              </Link>
            )}
            {isPrivate && isGuest && (
              <button
                type="button"
                className={liveCtaClass('gold')}
                onClick={async () => {
                  await supabase.auth.signOut({ scope: 'local' });
                  window.location.href = '/login';
                }}
              >
                Créer mon compte
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Héros : « GG ! », rang, podium. */}
          <div className="mt-2 flex flex-col items-center text-center">
            <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-[#f4c64e]">
              {liveLabel} · {levelLabel(niveau)}
            </p>
            <p className="text-ink-600 mt-1 line-clamp-1 text-sm">{title}</p>
            <h1
              className="font-display motion-safe:animate-bounce-in mt-3 -skew-x-6 text-7xl font-black leading-none tracking-tight text-white drop-shadow-[0_6px_28px_rgba(47,134,255,0.6)]"
              data-testid="live-finish-title"
            >
              {headline.title}
            </h1>
            <Swoosh />
            <p className="font-display mt-2 text-2xl font-extrabold text-white">{headline.line1}</p>
            {headline.line2 && <p className="text-ink-600 text-base">{headline.line2}</p>}
          </div>

          {podium.length > 0 && (
            <LivePodium
              rows={podium}
              unit={unit}
              size="lg"
              timeLabel={isPrivate ? 'moy.' : undefined}
              className="mt-12"
            />
          )}

          {/* Bilan : bonnes réponses, gain. */}
          <ArenaPanel className="mt-5 space-y-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[rgba(52,211,153,0.15)]">
                <CheckCircle2 className="h-6 w-6 text-[#34d399]" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="font-display text-base font-bold text-white">
                  {correctCount} bonne{correctCount > 1 ? 's' : ''} réponse
                  {correctCount > 1 ? 's' : ''} sur {stepCount}
                </p>
                <p className="text-ink-600 text-sm">
                  {headline.praise} {finalAppreciation(pct).emoji}
                </p>
              </div>
            </div>
            <div className="h-px bg-[rgba(255,255,255,0.1)]" />
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[rgba(244,198,78,0.15)]">
                <BarChart3 className="h-6 w-6 text-[#f4c64e]" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-xl font-extrabold tabular-nums text-white">
                  +{formatScore(totalScore)} {unit}
                </p>
                <p className="text-ink-600 text-sm">total gagné</p>
              </div>
              {xpEarned > 0 && (
                <span className="font-display rounded-full bg-[#0566f2] px-3 py-1 text-sm font-bold text-white">
                  +{xpEarned} XP
                </span>
              )}
            </div>
            <FinishNotes
              ranked={ranked}
              eligible={eligible}
              isGuest={isGuest}
              isPrivate={isPrivate}
              correctCount={correctCount}
              totalScore={totalScore}
              unit={unit}
              inPrizeZone={!!prize && ranked && eligible && myRank != null && myRank <= prize.top_n}
              prizeTopN={prize?.top_n ?? 0}
            />
          </ArenaPanel>

          {prize && <PrizeBanner prize={prize} className="mt-4" />}

          {/* Actions. */}
          <div className="mt-5 flex flex-col gap-3">
            {isPrivate && !isGuest && privateCode && (
              <Link
                to="/live/prive/nouveau"
                search={{ from: privateCode }}
                className={liveCtaClass('primary')}
              >
                <RefreshCw className="h-5 w-5" aria-hidden />
                Prendre ma revanche
              </Link>
            )}
            {isPrivate && isGuest && (
              <button
                type="button"
                className={liveCtaClass('gold')}
                onClick={async () => {
                  await supabase.auth.signOut({ scope: 'local' });
                  window.location.href = '/login';
                }}
              >
                Créer mon compte
              </button>
            )}
            <button
              type="button"
              aria-expanded={showCorrections}
              onClick={() => setShowCorrections((v) => !v)}
              className={liveCtaClass('glass')}
            >
              <FileText className="h-5 w-5" aria-hidden />
              {showCorrections ? 'Masquer les corrections' : 'Voir les corrections'}
            </button>
          </div>

          {showCorrections && (
            <div className="mt-4">
              <CorrectionsList steps={steps} unit={unit} />
            </div>
          )}

          <h2 className="font-display mb-3 mt-8 text-lg font-extrabold text-white">
            {isPrivate ? 'Classement du défi' : 'Classements'}
          </h2>
          {isPrivate ? (
            <PrivateLeaderboard board={privateBoard} unit={unit} paper />
          ) : (
            <LeaderboardTabs
              challengeId={challengeId}
              niveau={niveau}
              userId={userId}
              unit={unit}
              paper
            />
          )}
        </>
      )}

      {/* Défi privé (0722) : avis + SKILLCARDS du défi (le CTA du défi). */}
      {isPrivate && <PrivateLiveOutro challengeId={challengeId} isGuest={isGuest} />}

      {/* Bloc de test (indicateur + suppression) — tout en bas. */}
      {testMode && (
        <ArenaPanel className="mt-6 flex flex-col items-center gap-3 py-5 text-center">
          <p className="flex items-start gap-2 text-xs text-[#fadf90]">
            <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Mode test : ta participation figure dans le classement ci-dessus — supprime-la pour ne
              pas fausser le vrai défi.
            </span>
          </p>
          <Button
            variant="destructive"
            disabled={deleteTest.isPending || deleteTest.isSuccess}
            onClick={() =>
              deleteTest.mutate(challengeId, {
                onSuccess: () => {
                  window.location.href = '/student/news';
                },
              })
            }
          >
            {deleteTest.isSuccess
              ? 'Supprimée ✓'
              : deleteTest.isPending
                ? 'Suppression…'
                : 'Supprimer ma participation de test'}
          </Button>
          {deleteTest.isError && (
            <p className="text-error-600 text-xs">Échec de la suppression. Réessaie.</p>
          )}
        </ArenaPanel>
      )}
    </div>
  );
}

/** Mentions sous le bilan (classement, invité, HORA créditée, lot). */
function FinishNotes({
  ranked,
  eligible,
  isGuest,
  isPrivate,
  correctCount,
  totalScore,
  unit,
  inPrizeZone,
  prizeTopN,
}: {
  ranked: boolean;
  eligible: boolean;
  isGuest: boolean;
  isPrivate: boolean;
  correctCount: number;
  totalScore: number;
  unit: string;
  inPrizeZone: boolean;
  prizeTopN: number;
}) {
  const notes: ReactNode[] = [];
  if (!ranked) notes.push('Tu as joué sans classement (réservé aux élèves de ce niveau).');
  else if (!eligible) notes.push('Hors classement officiel (rejoint en cours).');
  if (isGuest)
    notes.push(
      'Tu as joué en invité : crée ton compte pour gagner des XP et des HORA au prochain défi.',
    );
  if (!isGuest && isPrivate && correctCount > 0)
    notes.push('L’HORA de chaque bonne réponse a été ajoutée à sa SKILLCARD.');
  if (!isPrivate && ranked && eligible && unit === 'HORA' && totalScore > 0)
    notes.push('Ton HORA a été ajoutée aux SKILLCARDS des questions réussies.');
  if (notes.length === 0 && !inPrizeZone) return null;
  return (
    <div className="space-y-1.5">
      {notes.map((n, i) => (
        <p key={i} className="text-ink-600 text-xs">
          {n}
        </p>
      ))}
      {inPrizeZone && (
        <p className="text-sm font-semibold text-[#f4c64e]">
          Tu es dans le top {prizeTopN} : la SKILLCARD arrive dans ton deck à la clôture du live. Si
          tu l’as déjà, tu choisiras celle que tu veux.
        </p>
      )}
    </div>
  );
}

/** Fenêtre d'interruption : le défi a été ouvert ailleurs avec ce compte. */
export function SupersededDialog({
  onTakeOver,
  onLeave,
}: {
  onTakeOver: () => void;
  onLeave: () => void;
}) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-10">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="live-superseded-title"
        aria-describedby="live-superseded-text"
        data-testid="live-superseded"
        className="w-full max-w-sm"
      >
        <ArenaPanel className="space-y-4 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[rgba(244,198,78,0.15)]">
            <MonitorX className="h-7 w-7 text-[#f4c64e]" aria-hidden />
          </span>
          <h2 id="live-superseded-title" className="font-display text-xl font-bold text-white">
            Défi ouvert ailleurs
          </h2>
          <p id="live-superseded-text" className="text-ink-600 text-sm">
            Tu viens d’ouvrir ce défi dans une autre fenêtre ou sur un autre appareil. On ne peut le
            jouer qu’à un seul endroit à la fois : cette fenêtre est mise en pause.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onTakeOver}
              className={liveCtaClass('primary', 'w-full')}
            >
              Jouer ici
            </button>
            <button type="button" onClick={onLeave} className={liveCtaClass('glass', 'w-full')}>
              Quitter
            </button>
          </div>
          <p className="text-ink-500 text-xs">« Jouer ici » met en pause l’autre fenêtre.</p>
        </ArenaPanel>
      </div>
    </div>
  );
}
